import { getKeys } from '@jack-henry/frontend-utils/functions';
import {
    DynamicComponentExitReason,
    DynamicComponentOptions,
    DynamicComponentResult,
} from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import { DeepPartial } from '@jack-henry/frontend-utils/types';
import { Nullable } from '@treasury/utils';
import type { LitElement, TemplateResult } from 'lit';

/**
 * Public API contract that all dialog services in TM apps should meet.
 *
 * This primarily exists so that we can use components relying on dialogs across
 * both legacy and modern apps without having a hard dependency on `TmBaseComponent`
 * (read: Tailwind).
 *
 * This interface can go away when `OmegaDialogService` is removed and only the one
 * `@treasury/presentation` remains.
 */
export interface IDialogService<DialogElement extends LitElement> {
    readonly isDialogOpen: boolean;
    open<T>(
        content: TmDialogContent<T>,
        title: string,
        options?: TmDialogServiceConfig<T>
    ): Promise<DynamicComponentResult<T>>;
    close<T>(reason?: DynamicComponentExitReason, data?: T): Promise<void>;
    getDialogElement(): DialogElement;
    getContent<E extends Element = Element>(): E;
    setDefaultConfiguration(config: Partial<TmDialogServiceConfig>): void;
}

type StaticContent = string | TemplateResult;
type SetDataFn<T> = (data: T) => void;

/**
 * @param setData Associate data with the currently open dialog.
 */
type TmDialogContentFn<T> = (setData: SetDataFn<T>) => StaticContent;

export type TmDialogContent<T> = StaticContent | TmDialogContentFn<T>;

export enum TmDialogButtonPosture {
    Primary = 'primary',
    Secondary = 'secondary',
    Tertiary = 'tertiary',
}

export type TmDialogButtonCloseFn<T = unknown> = (data?: T) => void;

export interface TmDialogButton<T = unknown> {
    label: string;
    reason: DynamicComponentExitReason;
    onClick?: (close: TmDialogButtonCloseFn<T>) => void;
    posture?: TmDialogButtonPosture;
    destructive?: boolean;
    checkDisabled?: (data?: T) => boolean;
}

export type TmPrefabDialogButton = Pick<TmDialogButton, 'label' | 'reason'>;

/**
 * Configuration for modifying prefabricated buttons.
 */
export type TmDialogButtonMap = Record<
    TmDialogButtonPosture.Primary | TmDialogButtonPosture.Secondary,
    TmPrefabDialogButton
>;

export type TmDialogOrientation = 'horizontal' | 'vertical';
/**
 * Internal config shape containing all possible settings specified.
 */
export interface TmDialogServiceConfigFull<T = unknown> extends Pick<
    DynamicComponentOptions,
    'host'
> {
    buttons: TmDialogButton<T>[] | TmDialogButtonMap;
    /**
     * Describes the orientation of the dialog buttons and title.
     *
     * A vertical orientation favors a centered, stacked layout, while
     * horizontal orientations favor a side-aligned layout.
     */
    orientation: TmDialogOrientation;
    /**
     * If `true`, an 'X' close button will be displayed in the top-right corner of the dialog.
     */
    showCloseButton: boolean;
    classes: string[];
    headerIcon?: TemplateResult<2>;
    preventClose: boolean;
    showButtons: boolean;
    /**
     * Whether or not to show a divider between the title header and the dialog content.
     */
    showDivider: boolean;
}

/**
 * Consumer-facing configuration contract. Most settings are optional.
 */
export interface TmDialogServiceConfig<T = unknown> extends Partial<
    Omit<TmDialogServiceConfigFull, 'buttons'>
> {
    /**
     * Specify an array of entirely custom buttons or
     * provide a map of buttons that adhere to standard dialog events.
     */
    buttons?: Partial<TmDialogButton<T>>[] | DeepPartial<Nullable<TmDialogButtonMap>>;
}

const DIALOG_CLOSE_EVENT = 'tm-dialog-close';

type TmDialogCloseEvent<T> = CustomEvent<DynamicComponentResult<T>>;

export function dispatchDialogCloseEvent<T>(element: Element, result: DynamicComponentResult<T>) {
    const closeEvent: TmDialogCloseEvent<T> = new CustomEvent(DIALOG_CLOSE_EVENT, {
        detail: result,
        bubbles: true,
        composed: true,
    });

    element.dispatchEvent(closeEvent);
}

export function listenForDialogResult<T>(
    element: Element,
    callback: (data: DynamicComponentResult<T>) => void
) {
    const listener = (e: Event) => {
        if (!(e instanceof CustomEvent)) {
            throw new Error(`Invalid event instance for "${DIALOG_CLOSE_EVENT}"`);
        }

        callback((e as TmDialogCloseEvent<T>).detail);
    };

    element.addEventListener(DIALOG_CLOSE_EVENT, listener);

    return {
        unsubscribe: () => element.removeEventListener(DIALOG_CLOSE_EVENT, listener),
    };
}

const DEFAULT_BUTTONS = Object.freeze<TmDialogButtonMap>({
    [TmDialogButtonPosture.Primary]: {
        label: 'OK',
        reason: DynamicComponentExitReason.Confirm,
    },
    [TmDialogButtonPosture.Secondary]: {
        label: 'Cancel',
        reason: DynamicComponentExitReason.Cancel,
    },
});

const DEFAULT_OPTIONS = Object.freeze<TmDialogServiceConfigFull>({
    buttons: DEFAULT_BUTTONS,
    classes: [],
    showButtons: true,
    showDivider: true,
    preventClose: true,
    showCloseButton: false,
    orientation: 'vertical',
    host: window.document.body,
});

/**
 * Normalize a partial configuration into a fully-filled configuration.
 */
export function normalizeConfig<T>(
    config: Partial<TmDialogServiceConfig<T>>
): TmDialogServiceConfigFull<T> {
    const buttons = normalizeButtonConfig(config.buttons);
    return {
        ...DEFAULT_OPTIONS,
        ...config,
        buttons,
    };
}

function normalizeButtonConfig<T>(
    buttons: TmDialogServiceConfig<T>['buttons'] = []
): TmDialogServiceConfigFull<T>['buttons'] {
    // if custom buttons are provided, use them verbatim,
    // filling in required details if missing
    if (Array.isArray(buttons)) {
        const normalizedButtons = buttons.map(button => {
            const posture = button.posture || mapReasonToDefaultPosture(button.reason);
            /**
             * If given a valid posture, use it to find the default button.
             * Otherwise, assume primary.
             */
            const defaultButton =
                posture === TmDialogButtonPosture.Primary ||
                posture === TmDialogButtonPosture.Secondary
                    ? DEFAULT_BUTTONS[posture]
                    : DEFAULT_BUTTONS[TmDialogButtonPosture.Primary];

            return {
                ...defaultButton,
                posture,
                ...button,
            };
        });

        return normalizedButtons.length > 0 ? normalizedButtons : DEFAULT_BUTTONS;
    }

    // otherwise, use prefab buttons for missing postures
    return getKeys(DEFAULT_BUTTONS).reduce(
        (buttonMap, posture) => {
            const button = buttons[posture];

            // support explicitly removing buttons with null value
            // eslint-disable-next-line no-restricted-syntax
            if (button === null) {
                return buttonMap;
            }

            const defaultButton = DEFAULT_BUTTONS[posture];

            buttonMap[posture] = {
                ...defaultButton,
                ...button,
            };

            return buttonMap;
        },
        { ...DEFAULT_BUTTONS }
    );
}

function mapReasonToDefaultPosture(reason?: DynamicComponentExitReason) {
    if (!reason) {
        return TmDialogButtonPosture.Primary;
    }

    switch (reason) {
        case DynamicComponentExitReason.Confirm:
            return TmDialogButtonPosture.Primary;
        case DynamicComponentExitReason.Cancel:
            return TmDialogButtonPosture.Secondary;
        case DynamicComponentExitReason.ForceClose:
            return TmDialogButtonPosture.Tertiary;
        default:
            throw new Error(`Could not map default posture for reason: "${reason}"`);
    }
}
